import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Crown, Star, Users, Settings, Edit3, CreditCard, CheckCircle2, QrCode, UserPlus, Shield, Trash2, Download, Cookie } from 'lucide-react';
import { openCookiePreferences } from '../CookiePreferencesLink';
import { Button } from '../ui/button';
import { QRCodeSVG } from 'qrcode.react';
import MedicoSettings from '../MedicoSettings';
import { supabase as supabaseClient } from '../../contexts/SupabaseAuthContext';
import { DeleteAccountButton } from './DeleteAccountButton';
import { isPaidPlan, openStripePortal } from '../../lib/stripePortal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsTab = ({ famiglia, supabase, onUpdate, user, onEditConfig }) => {
  const [userPlan, setUserPlan] = useState({ plan: 'base', plan_name: 'Base', features: [], recipients: { limit: 1 } });
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [nucleoMembers, setNucleoMembers] = useState([]);
  const [loadingNucleo, setLoadingNucleo] = useState(true);
  const [showQR, setShowQR] = useState(false);

  // Genera il link di invito per il nucleo familiare
  const inviteLink = famiglia?.id ? `${window.location.origin}/join-family/${famiglia.id}` : '';

  const loadPlan = async () => {
    if (!user?.id) return;
    try {
      const response = await fetch(`${API_URL}/api/user/${user.id}/plan`);
      if (response.ok) {
        const data = await response.json();
        setUserPlan(data);
      }
    } catch (error) {
      console.error('Error loading plan:', error);
    }
  };

  // Carica i membri del nucleo familiare
  const loadNucleoMembers = async () => {
    if (!famiglia?.id) return;
    setLoadingNucleo(true);
    try {
      const response = await fetch(`${API_URL}/api/famiglia/${famiglia.id}/nucleo`);
      if (response.ok) {
        const data = await response.json();
        setNucleoMembers(data.members || []);
      }
    } catch (error) {
      console.error('Error loading nucleo:', error);
      // Fallback: mostra solo l'admin
      setNucleoMembers([{ id: user?.id, email: user?.email, role: 'admin', name: 'Tu (Admin)' }]);
    } finally {
      setLoadingNucleo(false);
    }
  };

  useEffect(() => {
    loadPlan();
    loadNucleoMembers();
  }, [user, famiglia]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user?.id) {
        loadPlan();
        loadNucleoMembers();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, famiglia]);

  const handleUpgrade = async (plan) => {
    setLoadingPlan(plan);
    try {
      const response = await fetch(`${API_URL}/api/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: plan,
          billing_period: 'monthly',
          origin_url: window.location.origin,
          user_id: user?.id,
          email: user?.email
        })
      });

      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        const errorMsg = typeof data.detail === 'string' ? data.detail : 'Errore durante il checkout';
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error(error.message || 'Errore nell\'avvio del pagamento');
    } finally {
      setLoadingPlan(null);
    }
  };

  const copyInviteLink = async () => {
    await navigator.clipboard.writeText(inviteLink);
    toast.success('Link di invito copiato!');
  };

  const isPremium = userPlan.plan === 'premium';
  
  // Calcola slot disponibili in base al piano (DESTINATARI, non membri)
  const getMaxSlots = () => {
    if (userPlan.plan === 'premium') return 2; // Premium ha 2 destinatari
    return 1; // Tutti gli altri piani: 1 destinatario
  };
  
  const maxSlots = getMaxSlots();
  const usedSlots = nucleoMembers.length;
  const availableSlots = maxSlots - usedSlots;

  const getPlanBadge = () => {
    if (userPlan.plan === 'premium') {
      return <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-[#F59E0B] to-[#EF4444] text-white text-xs font-bold rounded-full"><Crown className="w-3 h-3" />PREMIUM</span>;
    }
    if (userPlan.plan === 'standard') {
      return <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#4A7C59] text-white text-xs font-bold rounded-full"><Star className="w-3 h-3" />STANDARD</span>;
    }
    if (userPlan.plan === 'fondatori' || userPlan.plan === 'founder') {
      return <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-[#8B5CF6] to-[#6366F1] text-white text-xs font-bold rounded-full"><Star className="w-3 h-3" />FONDATORE</span>;
    }
    return <span className="px-3 py-1 bg-[#E5E7EB] text-[#64748B] text-xs font-bold rounded-full">BASE</span>;
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-[#2C3E50]">Impostazioni</h2>

      {/* ==================== NUCLEO FAMILIARE ==================== */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        <div className="bg-gradient-to-r from-[#4A7C59] to-[#3A6345] p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white">Destinatari Notifiche</h3>
              <p className="text-white/80 text-sm">
                {usedSlots}/{maxSlots} destinatari attivi
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Lista membri */}
          <div className="space-y-2">
            {loadingNucleo ? (
              <div className="text-center py-4 text-[#64748B]">Caricamento...</div>
            ) : (
              <>
                {nucleoMembers.map((member, index) => (
                  <div key={member.id || index} className="flex items-center justify-between p-3 bg-[#F9FAFB] rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${member.role === 'admin' ? 'bg-[#4A7C59]' : 'bg-[#64748B]'}`}>
                        {member.role === 'admin' ? (
                          <Shield className="w-5 h-5 text-white" />
                        ) : (
                          <Users className="w-5 h-5 text-white" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-[#2C3E50]">
                          {member.name || member.email?.split('@')[0] || `Membro ${index + 1}`}
                        </p>
                        <p className="text-xs text-[#64748B]">
                          {member.role === 'admin' ? 'Amministratore' : 'Familiare'} 
                          {member.fcm_token && ' • Notifiche attive'}
                        </p>
                      </div>
                    </div>
                    {member.role === 'admin' && (
                      <span className="px-2 py-1 bg-[#4A7C59]/10 text-[#4A7C59] text-xs font-bold rounded-full">
                        ADMIN
                      </span>
                    )}
                  </div>
                ))}

                {/* Slot vuoti */}
                {availableSlots > 0 && maxSlots > 1 && (
                  <div 
                    className="flex items-center justify-center p-4 border-2 border-dashed border-[#E5E7EB] rounded-xl cursor-pointer hover:border-[#4A7C59] hover:bg-[#F9FAFB] transition-all"
                    onClick={() => setShowQR(true)}
                  >
                    <div className="text-center">
                      <UserPlus className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
                      <p className="text-sm text-[#64748B]">
                        {availableSlots} slot disponibil{availableSlots > 1 ? 'i' : 'e'}
                      </p>
                      <p className="text-xs text-[#4A7C59] font-semibold">Clicca per invitare</p>
                    </div>
                  </div>
                )}

                {/* Se piano base, mostra upgrade */}
                {userPlan.plan !== 'premium' && (
                  <div className="p-4 bg-gradient-to-br from-[#FEF3C7] to-[#FEF9C3] rounded-xl border border-[#FCD34D]">
                    <p className="text-sm text-[#92400E] mb-3">
                      <strong>Vuoi condividere le notifiche con altri familiari?</strong>
                    </p>
                    <p className="text-xs text-[#B45309] mb-3">
                      Passa a Premium (2 destinatari) per invitare un altro familiare.
                    </p>
                    <div className="flex gap-2">
                      <Button onClick={() => handleUpgrade('standard')} disabled={loadingPlan === 'standard'} size="sm" className="flex-1 bg-[#4A7C59] hover:bg-[#3A6345] text-white">
                        Standard €15
                      </Button>
                      <Button onClick={() => handleUpgrade('premium')} disabled={loadingPlan === 'premium'} size="sm" className="flex-1 bg-gradient-to-r from-[#F59E0B] to-[#EF4444] text-white">
                        Premium €21
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* QR Code e Link Invito */}
          {showQR && availableSlots > 0 && (
            <div className="p-4 bg-[#F9FAFB] rounded-xl space-y-4">
              <div className="text-center">
                <h4 className="font-bold text-[#2C3E50] mb-2">Invita un Familiare</h4>
                <p className="text-sm text-[#64748B] mb-4">
                  Mostra questo QR code o condividi il link. Chi lo scansiona verrà aggiunto automaticamente!
                </p>
                
                {/* QR Code */}
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-white rounded-xl border-2 border-[#4A7C59] shadow-lg">
                    <QRCodeSVG 
                      value={inviteLink} 
                      size={180}
                      level="H"
                      fgColor="#1B4332"
                    />
                  </div>
                </div>

                {/* Link copiabile */}
                <div className="flex items-center gap-2 bg-white rounded-xl border border-[#E5E7EB] p-2">
                  <p className="flex-1 px-2 font-mono text-xs text-gray-600 truncate">
                    {inviteLink}
                  </p>
                  <Button onClick={copyInviteLink} size="sm" className="bg-[#4A7C59] hover:bg-[#3A6345]">
                    Copia
                  </Button>
                </div>
              </div>
              
              <Button onClick={() => setShowQR(false)} variant="ghost" className="w-full">
                Chiudi
              </Button>
            </div>
          )}

          {/* Bottone per mostrare QR */}
          {!showQR && availableSlots > 0 && maxSlots > 1 && (
            <Button onClick={() => setShowQR(true)} className="w-full h-12 bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl">
              <QrCode className="w-5 h-5 mr-2" />
              Invita con QR Code
            </Button>
          )}
        </div>
      </div>

      {/* ==================== PIANO ATTUALE ==================== */}
      <div className="bg-gradient-to-br from-[#FAF9F6] to-white rounded-2xl p-6 border border-[#E5E7EB]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm text-[#64748B]">Piano attuale</p>
            <h3 className="text-2xl font-bold text-[#2C3E50]">{userPlan.plan_name || 'Prova Gratuita'}</h3>
          </div>
          {getPlanBadge()}
        </div>
        
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="px-3 py-1 bg-[#E8F5E9] text-[#4A7C59] text-xs font-medium rounded-full">
            ✓ Notifiche illimitate
          </span>
          <span className="px-3 py-1 bg-[#E3F2FD] text-[#1976D2] text-xs font-medium rounded-full">
            ✓ Accesso {userPlan.plan === 'trial' ? '7 giorni' : 'illimitato'}
          </span>
          <span className="px-3 py-1 bg-[#F3E5F5] text-[#7B1FA2] text-xs font-medium rounded-full">
            ✓ {maxSlots} {maxSlots === 1 ? 'destinatario' : 'destinatari'}
          </span>
          {isPremium && (
            <span className="px-3 py-1 bg-[#FFF3E0] text-[#E65100] text-xs font-medium rounded-full">
              ✓ Tasto Scudo
            </span>
          )}
          {isPremium && (
            <span className="px-3 py-1 bg-[#E8EAF6] text-[#3F51B5] text-xs font-medium rounded-full">
              ✓ GPS Tracking
            </span>
          )}
        </div>

        {!['premium', 'standard', 'fondatori', 'founder'].includes(userPlan.plan) && (
          <div className="pt-4 border-t border-[#E5E7EB] space-y-3">
            <p className="text-sm font-semibold text-[#2C3E50]">Scegli un piano per sbloccare tutte le funzionalità</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button onClick={() => handleUpgrade('standard')} disabled={loadingPlan === 'standard'} className="h-auto py-3 bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl flex flex-col items-center justify-center">
                {loadingPlan === 'standard' ? 'Caricamento...' : (
                  <>
                    <span className="flex items-center"><Star className="w-4 h-4 mr-2" />Standard</span>
                    <span className="text-lg font-bold">€15/mese</span>
                    <span className="text-xs opacity-80">1 destinatario · Scudo</span>
                  </>
                )}
              </Button>
              <Button onClick={() => handleUpgrade('premium')} disabled={loadingPlan === 'premium'} className="h-auto py-3 bg-gradient-to-r from-[#F59E0B] to-[#EF4444] text-white rounded-xl flex flex-col items-center justify-center">
                {loadingPlan === 'premium' ? 'Caricamento...' : (
                  <>
                    <span className="flex items-center"><Crown className="w-4 h-4 mr-2" />Premium</span>
                    <span className="text-lg font-bold">€21/mese</span>
                    <span className="text-xs opacity-80">2 destinatari · Team + Passeggiate + Stazione Digitale</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {['standard', 'fondatori', 'founder'].includes(userPlan.plan) && (
          <div className="pt-4 border-t border-[#E5E7EB] space-y-3">
            <p className="text-sm font-semibold text-[#2C3E50]">Vuoi più funzionalità?</p>
            <Button onClick={() => handleUpgrade('premium')} disabled={loadingPlan === 'premium'} className="w-full h-12 bg-gradient-to-r from-[#F59E0B] to-[#EF4444] text-white rounded-xl">
              {loadingPlan === 'premium' ? 'Caricamento...' : <><Crown className="w-4 h-4 mr-2" />Passa a Premium - €21/mese (Team + Passeggiate)</>}
            </Button>
          </div>
        )}

        <Button variant="outline" className="w-full h-12 mt-4 rounded-xl border-[#E5E7EB]">
          <CreditCard className="w-4 h-4 mr-2" />
          Gestisci Abbonamento
        </Button>
        <p className="text-center text-xs text-[#9CA3AF] mt-2">
          Scarica fatture, modifica metodo di pagamento o cancella il piano
        </p>
      </div>

      {/* ==================== MODIFICA CONFIGURAZIONE ==================== */}
      <div className="bg-gradient-to-br from-[#FEF3C7] to-[#FEF9C3] rounded-2xl p-6 border border-[#FCD34D]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#F59E0B]/20 rounded-xl flex items-center justify-center">
              <Settings className="w-6 h-6 text-[#F59E0B]" />
            </div>
            <div>
              <h3 className="font-bold text-[#92400E]">Modifica Configurazione</h3>
              <p className="text-sm text-[#B45309]">Cambia dati famiglia o medicine</p>
            </div>
          </div>
          <Button onClick={onEditConfig} className="bg-[#F59E0B] hover:bg-[#E78A08] text-white rounded-xl">
            <Edit3 className="w-4 h-4 mr-2" />
            Modifica
          </Button>
        </div>
      </div>

      {/* ==================== RICETTE PREMIUM ==================== */}
      <MedicoSettings 
        famiglia={famiglia}
        supabase={supabase}
        isPremium={isPremium}
        onUpgrade={() => handleUpgrade('premium')}
      />

      {/* ==================== PRIVACY E DATI (GDPR) ==================== */}
      <div className="bg-white rounded-2xl p-6 border border-[#E5E7EB]">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-[#EDE9FE] rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-[#7C3AED]" />
          </div>
          <div>
            <h3 className="font-bold text-[#2C3E50]">Privacy e Dati Personali</h3>
            <p className="text-sm text-[#64748B]">Gestisci i tuoi dati secondo il GDPR</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Preferenze cookie: cambiare o ritirare il consenso in qualsiasi momento */}
          <div className="flex items-center justify-between p-4 bg-[#F8FAFC] rounded-xl">
            <div className="flex items-center gap-3">
              <Cookie className="w-5 h-5 text-[#7C3AED]" />
              <div>
                <p className="font-medium text-[#2C3E50]">Preferenze cookie</p>
                <p className="text-xs text-[#64748B]">Cambia o ritira il tuo consenso ai cookie</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={openCookiePreferences} data-testid="settings-cookie-preferences">
              Modifica
            </Button>
          </div>

          {/* Export Dati */}
          <div className="flex items-center justify-between p-4 bg-[#F8FAFC] rounded-xl">
            <div className="flex items-center gap-3">
              <Download className="w-5 h-5 text-[#4A7C59]" />
              <div>
                <p className="font-medium text-[#2C3E50]">Esporta i tuoi dati</p>
                <p className="text-xs text-[#64748B]">Scarica tutti i tuoi dati in formato JSON</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={async () => {
                try {
                  const { data: { session } } = await supabaseClient.auth.getSession();
                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/privacy/export/${famiglia.id}`, {
                    headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}
                  });
                  if (response.ok) {
                    const data = await response.json();
                    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `curacheck_dati_${new Date().toISOString().split('T')[0]}.json`;
                    a.click();
                    toast.success('Dati esportati con successo!');
                  } else {
                    toast.error('Errore nell\'esportazione dei dati');
                  }
                } catch (err) {
                  toast.error('Errore di connessione');
                }
              }}
              className="rounded-xl"
            >
              Scarica
            </Button>
          </div>

          {/* Gestisci Abbonamento Stripe */}
          {isPaidPlan(userPlan.plan) && (
            <div className="flex items-center justify-between p-4 bg-[#F0F9FF] rounded-xl border border-blue-100">
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="font-medium text-blue-700">Gestisci Abbonamento</p>
                  <p className="text-xs text-blue-500">Modifica, aggiorna o disdici il tuo piano</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => openStripePortal(famiglia.id, {
                  onNoCustomer: () => toast.info('Non è possibile gestire l\'abbonamento: non risulta un abbonamento Stripe attivo.')
                })}
                data-testid="settings-manage-subscription-btn"
                className="rounded-xl border-blue-300 text-blue-600 hover:bg-blue-50"
              >
                Gestisci
              </Button>
            </div>
          )}

          {/* Elimina Account */}
          <div className="flex items-center justify-between p-4 bg-[#FEF2F2] rounded-xl border border-red-100">
            <div className="flex items-center gap-3">
              <Trash2 className="w-5 h-5 text-red-500" />
              <div>
                <p className="font-medium text-red-700">Elimina Account</p>
                <p className="text-xs text-red-500">Cancella definitivamente tutti i tuoi dati</p>
              </div>
            </div>
            <DeleteAccountButton famiglia={famiglia} />
          </div>
        </div>

        <p className="text-xs text-[#9CA3AF] mt-4 text-center">
          I tuoi diritti GDPR: accesso, rettifica, cancellazione, portabilità dei dati
        </p>
      </div>
    </div>
  );
};

export default SettingsTab;
